import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Search, X } from "lucide-react";
import { Button } from "@/components/ui/Button";

// Self-drawn picker on Tencent JS API GL (map.qq.com/api/gljs). Unlike the
// official locpicker component, a raw map click anywhere — including empty
// land with no POI — yields coordinates (GCJ-02, matching the backend's
// Tencent Map pipeline). Search uses the WebService suggestion endpoint via
// JSONP with the same key. All TMap APIs below verified against the real SDK.

export interface PickedLocation {
  lat: number;
  lng: number;
  poiname: string;
  poiaddress: string;
}

interface Props {
  open: boolean;
  /** Tencent map key from the backend: null = still loading, "" = unconfigured. */
  mapKey: string | null;
  /** Current form values; when valid they seed the map center + marker. */
  initialLat: string;
  initialLng: string;
  onPick: (loc: PickedLocation) => void;
  onClose: () => void;
}

// Minimal typings for the parts of the GL SDK we use.
interface TMapLatLng {
  getLat(): number;
  getLng(): number;
}
interface TMapMap {
  on(event: "click", handler: (e: { latLng: TMapLatLng }) => void): void;
  setCenter(position: TMapLatLng): void;
  destroy(): void;
}
interface TMapMultiMarker {
  setGeometries(geometries: { id: string; position: TMapLatLng }[]): void;
}
interface TMapNamespace {
  Map: new (el: HTMLElement, opts: { center: TMapLatLng; zoom: number }) => TMapMap;
  LatLng: new (lat: number, lng: number) => TMapLatLng;
  MultiMarker: new (opts: { map: TMapMap; geometries: unknown[] }) => TMapMultiMarker;
}
declare global {
  interface Window {
    TMap?: TMapNamespace;
  }
}

let glLoader: Promise<TMapNamespace> | null = null;

function loadTMapGL(key: string): Promise<TMapNamespace> {
  if (window.TMap) return Promise.resolve(window.TMap);
  if (!glLoader) {
    glLoader = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://map.qq.com/api/gljs?v=1.exp&key=${encodeURIComponent(key)}`;
      script.onload = () => {
        if (window.TMap) resolve(window.TMap);
        else reject(new Error("TMap global missing after script load"));
      };
      script.onerror = () => {
        glLoader = null; // allow retry on next open
        reject(new Error("failed to load Tencent map SDK"));
      };
      document.head.appendChild(script);
    });
  }
  return glLoader;
}

interface SuggestionItem {
  title: string;
  address: string;
  lat: number;
  lng: number;
}

let jsonpSeq = 0;

// WebService APIs are CORS-less; the documented browser path is output=jsonp.
function fetchSuggestions(key: string, keyword: string): Promise<SuggestionItem[]> {
  return new Promise((resolve, reject) => {
    const cb = `__tmap_suggest_${++jsonpSeq}`;
    const holder = window as unknown as Record<string, unknown>;
    const script = document.createElement("script");
    const cleanup = (): void => {
      delete holder[cb];
      script.remove();
    };
    holder[cb] = (payload: unknown): void => {
      cleanup();
      resolve(parseSuggestions(payload));
    };
    script.onerror = () => {
      cleanup();
      reject(new Error("suggestion request failed"));
    };
    const params = new URLSearchParams({
      keyword,
      key,
      output: "jsonp",
      callback: cb,
      page_size: "8",
    });
    script.src = `https://apis.map.qq.com/ws/place/v1/suggestion?${params.toString()}`;
    document.head.appendChild(script);
  });
}

// Suggestion payload is untrusted boundary input — validate shape before use.
function parseSuggestions(payload: unknown): SuggestionItem[] {
  if (typeof payload !== "object" || payload === null) return [];
  const data = (payload as Record<string, unknown>).data;
  if (!Array.isArray(data)) return [];
  const items: SuggestionItem[] = [];
  for (const entry of data) {
    if (typeof entry !== "object" || entry === null) continue;
    const rec = entry as Record<string, unknown>;
    const location = rec.location;
    if (typeof location !== "object" || location === null) continue;
    const { lat, lng } = location as Record<string, unknown>;
    if (typeof lat !== "number" || typeof lng !== "number") continue;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    items.push({
      title: typeof rec.title === "string" ? rec.title : "",
      address: typeof rec.address === "string" ? rec.address : "",
      lat,
      lng,
    });
  }
  return items;
}

const DEFAULT_CENTER = { lat: 39.9088, lng: 116.3975 };

export function MapPickerModal({
  open,
  mapKey,
  initialLat,
  initialLng,
  onPick,
  onClose,
}: Props): JSX.Element | null {
  const [picked, setPicked] = useState<PickedLocation | null>(null);
  const [mapState, setMapState] = useState<"loading" | "ready" | "error">("loading");
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [searching, setSearching] = useState(false);

  const mapElRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<TMapMap | null>(null);
  const markerRef = useRef<TMapMultiMarker | null>(null);
  // Initial coords are read once per open; a ref keeps the map-init effect
  // from depending on (and re-creating the map for) every form keystroke.
  const initialRef = useRef({ lat: initialLat, lng: initialLng });
  useEffect(() => {
    initialRef.current = { lat: initialLat, lng: initialLng };
  });

  useEffect(() => {
    if (!open) return;
    setPicked(null);
    setQuery("");
    setSuggestions([]);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !mapKey) return;
    let disposed = false;
    setMapState("loading");
    loadTMapGL(mapKey)
      .then((TMap) => {
        if (disposed || !mapElRef.current) return;
        const lat0 = Number(initialRef.current.lat);
        const lng0 = Number(initialRef.current.lng);
        const hasInitial =
          initialRef.current.lat.trim() !== "" &&
          initialRef.current.lng.trim() !== "" &&
          Number.isFinite(lat0) &&
          Number.isFinite(lng0) &&
          Math.abs(lat0) <= 90 &&
          Math.abs(lng0) <= 180;
        const center = new TMap.LatLng(
          hasInitial ? lat0 : DEFAULT_CENTER.lat,
          hasInitial ? lng0 : DEFAULT_CENTER.lng,
        );
        const map = new TMap.Map(mapElRef.current, { center, zoom: hasInitial ? 14 : 11 });
        const marker = new TMap.MultiMarker({ map, geometries: [] });
        if (hasInitial) {
          marker.setGeometries([{ id: "picked", position: center }]);
          setPicked({ lat: lat0, lng: lng0, poiname: "", poiaddress: "" });
        }
        map.on("click", (e) => {
          const lat = e.latLng.getLat();
          const lng = e.latLng.getLng();
          marker.setGeometries([{ id: "picked", position: new TMap.LatLng(lat, lng) }]);
          setPicked({ lat, lng, poiname: "", poiaddress: "" });
        });
        mapRef.current = map;
        markerRef.current = marker;
        setMapState("ready");
      })
      .catch(() => {
        if (!disposed) setMapState("error");
      });
    return () => {
      disposed = true;
      markerRef.current = null;
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
    };
  }, [open, mapKey]);

  useEffect(() => {
    if (!open || !mapKey) return;
    const keyword = query.trim();
    if (keyword.length < 2) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      setSearching(true);
      fetchSuggestions(mapKey, keyword)
        .then(setSuggestions)
        .catch(() => setSuggestions([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [open, mapKey, query]);

  if (!open) return null;

  const selectSuggestion = (item: SuggestionItem): void => {
    const TMap = window.TMap;
    if (!TMap || !mapRef.current || !markerRef.current) return;
    const pos = new TMap.LatLng(item.lat, item.lng);
    mapRef.current.setCenter(pos);
    markerRef.current.setGeometries([{ id: "picked", position: pos }]);
    setPicked({ lat: item.lat, lng: item.lng, poiname: item.title, poiaddress: item.address });
    setSuggestions([]);
    setQuery(item.title);
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-zinc-900/30 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex h-[80vh] w-[860px] max-w-[92vw] flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="地图选点"
      >
        <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <MapPin className="h-4 w-4 flex-shrink-0 text-brand-500" />
            <span className="text-[13px] font-medium text-zinc-800">地图选点（腾讯地图）</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
            title="关闭 (Esc)"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="relative min-h-0 flex-1 bg-zinc-100">
          {mapKey === "" ? (
            <MissingKeyNotice />
          ) : (
            <>
              {/* z-0 gives the container its own stacking context: TMap injects
                  an internal z-index:1000 layer that would otherwise sit above
                  the z-10 search overlay and swallow its clicks. */}
              <div ref={mapElRef} className="absolute inset-0 z-0" />
              {mapState !== "ready" || mapKey === null ? (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-zinc-50/90 text-[13px] text-zinc-500">
                  {mapState === "error" ? (
                    "地图加载失败，请关闭后重试"
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      正在加载地图…
                    </span>
                  )}
                </div>
              ) : null}
              <div className="absolute left-3 top-3 z-10 w-[300px]">
                <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2.5 shadow-sm">
                  {searching ? (
                    <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-zinc-400" />
                  ) : (
                    <Search className="h-3.5 w-3.5 flex-shrink-0 text-zinc-400" />
                  )}
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="搜索地点（也可直接点击地图）"
                    className="h-9 w-full bg-transparent text-[13px] text-zinc-800 placeholder:text-zinc-400 focus:outline-none"
                  />
                  {query ? (
                    <button
                      type="button"
                      onClick={() => {
                        setQuery("");
                        setSuggestions([]);
                      }}
                      className="rounded p-0.5 text-zinc-400 hover:text-zinc-600"
                      aria-label="清空搜索"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
                {suggestions.length > 0 ? (
                  <div className="mt-1 max-h-72 overflow-auto rounded-md border border-zinc-200 bg-white py-1 shadow-lg">
                    {suggestions.map((item, index) => (
                      <button
                        key={`${item.title}-${index}`}
                        type="button"
                        onClick={() => selectSuggestion(item)}
                        className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-zinc-50"
                      >
                        <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-zinc-300" />
                        <span className="min-w-0">
                          <span className="block truncate text-[13px] text-zinc-800">
                            {item.title}
                          </span>
                          <span className="block truncate text-[11px] text-zinc-400">
                            {item.address}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-zinc-200 px-4 py-2.5">
          <div className="min-w-0 text-[12px] text-zinc-600">
            {picked ? (
              <span
                className="block truncate"
                title={`${picked.poiname} ${picked.poiaddress}`.trim()}
              >
                已选：{picked.poiname || picked.poiaddress || "地图点位"} ·{" "}
                <span className="font-mono">
                  {picked.lat.toFixed(6)}, {picked.lng.toFixed(6)}
                </span>
              </span>
            ) : (
              <span className="text-zinc-400">
                点击地图任意位置（空地也可以），或搜索地点后从列表选择。
              </span>
            )}
          </div>
          <Button
            size="sm"
            disabled={!picked}
            onClick={() => {
              if (picked) onPick(picked);
            }}
          >
            使用该坐标
          </Button>
        </footer>
      </div>
    </div>
  );
}

function MissingKeyNotice(): JSX.Element {
  return (
    <div className="flex h-full items-center justify-center bg-zinc-50 px-6">
      <div className="max-w-md rounded-xl border border-zinc-200 bg-white p-6 text-center shadow-sm">
        <MapPin className="mx-auto mb-3 h-8 w-8 text-zinc-400" />
        <div className="text-sm font-medium text-zinc-800">未配置腾讯地图 Key</div>
        <p className="mt-2 text-xs leading-5 text-zinc-500">
          后端 .env 中未配置 TENCENT_MAP_KEY。配置后重启服务即可使用地图选点；Key 需在 lbs.qq.com
          控制台启用 WebServiceAPI 与 JavaScriptAPI GL。
        </p>
      </div>
    </div>
  );
}
